import React, {useMemo} from 'react'
import styled from 'styled-components'
import {IMAGE_ASSET, WINDOW_MODE_TABLET_WIDTH} from 'consts'
import {useTranslation} from 'i18n'
import {getBorder, getBoxShadow, getFontFamily, getFontSize} from 'utils'
import {Icon, Image, Paragraph, ParsedText} from 'common/components'
import convertUnit from 'lib/unit'
import {FototreeTallestTreeListItemProps} from './FototreeTallestTreeProps'

interface StyledRankLabelProps {
  isRankChange?: boolean
}

const StyledContainer = styled.div`
  display: flex;
  align-items: center;
  box-sizing: border-box;
  background-color: ${({theme}) => theme.white_1};
  border-radius: ${convertUnit(8)};
  padding: ${convertUnit(6)} ${convertUnit(8)};
  margin: ${convertUnit(2)} 0;
  height: ${convertUnit(48)};
  gap: ${convertUnit(8)};
  box-shadow: ${({theme}) =>
    getBoxShadow(theme, {
      verticalOffset: 2,
      blurRadius: 4,
      opacity: 0.24,
    })};
  cursor: pointer;
`

const StyledInfoContainer = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: ${convertUnit(2.5)};
  justify-content: center;
`

const StyledInfoBottomContainer = styled.div`
  display: flex;
  align-items: baseline;
  gap: ${convertUnit(4)};
`

const StyledImageContainer = styled.div`
  position: relative;
  border-radius: ${convertUnit(4)};
  border: ${({theme}) => getBorder(0.5, 'solid', theme.gray_4)};
`

const StyledParagraph = styled(Paragraph)`
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
  word-break: break-all;
`

const StyledImage = styled(Image)`
  width: ${convertUnit(28)};
  aspect-ratio: 0.75;
  object-fit: contain;
`

const StyledBold = styled.span`
  font-family ${getFontFamily('bold')};
  font-size: ${getFontSize('xs')};
`

const StyledIconContainer = styled.div`
  position: absolute;
`

const StyledRankContainer = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  width: ${convertUnit(32)};
  height: ${convertUnit(32)};
`

const StyledRankLabel = styled.p<StyledRankLabelProps>`
  ${({theme, isRankChange}) => ({
    marginLeft: isRankChange ? convertUnit(-2) : convertUnit(2),
    color: isRankChange ? theme.gray_4 : theme.black,
  })}
  margin-top: 0;
  margin-bottom: 0;
  font-family ${getFontFamily('bold')};
  font-size: ${getFontSize('xxs')};
  z-index: 2;

  @media (min-width: ${WINDOW_MODE_TABLET_WIDTH}px) {
    @-moz-document url-prefix() {
      margin-top: ${convertUnit(-2)};
    }
  }
`

const StyledRankChangeContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: ${convertUnit(-4)};
`

export default function FototreeTallestTreeListItem({
  rank,
  data,
  onClickItem,
}: FototreeTallestTreeListItemProps) {
  const defaultImage = IMAGE_ASSET('tree', 'default.png')
  const {translate} = useTranslation()

  const handleRankLabel = useMemo(
    () => (
      <div>
        <StyledRankContainer>
          <StyledIconContainer>
            <Icon type="leaf-rank" size={convertUnit(32)} color="warning_4" />
          </StyledIconContainer>
          <StyledRankLabel>{rank}</StyledRankLabel>
        </StyledRankContainer>
        {data.type !== 'ALL' && !!data.rank_change && (
          <StyledRankChangeContainer>
            <Icon
              type={data.rank_change > 0 ? 'rank-up' : 'rank-down'}
              size={convertUnit(12)}
              marginLeft={convertUnit(-4)}
              color="gray_4"
            />
            <StyledRankLabel isRankChange>
              {Math.abs(data.rank_change)}
            </StyledRankLabel>
          </StyledRankChangeContainer>
        )}
      </div>
    ),
    [data, rank],
  )

  const handleRenderImage = useMemo(
    () => (
      <StyledImageContainer>
        <StyledImage
          alt={data.type === 'ALL' ? data.name : data.tree_name}
          src={data.url}
          fallbackSrc={defaultImage}
        />
      </StyledImageContainer>
    ),
    [data, defaultImage],
  )

  const handleRenderInfo = useMemo(
    () => (
      <StyledInfoContainer>
        <StyledParagraph fontWeight="medium">
          {data.type === 'ALL' ? data.name : data.tree_name}
        </StyledParagraph>
        <StyledInfoBottomContainer>
          <Paragraph fontSize="xs" color="primary_5">
            <ParsedText
              parse={[
                {
                  pattern: /<bold>.*?<\/bold>/,
                  style: {fontFamily: getFontFamily('bold')},
                  renderText: (text) => text.replace(/<bold>|<\/bold>/g, ''),
                },
              ]}>
              {translate('tree:leafCount', {
                count: data.leaf_count,
              })}
            </ParsedText>
          </Paragraph>
          <Paragraph fontSize="xs" color="primary_5">
            |
          </Paragraph>
          <Paragraph fontSize="xs" color="primary_5">
            <StyledBold>{data.height || 0}</StyledBold>{' '}
            {translate('tree:treeHeight')}
          </Paragraph>
        </StyledInfoBottomContainer>
      </StyledInfoContainer>
    ),
    [data, translate],
  )

  return (
    <StyledContainer onClick={() => onClickItem(data)}>
      {handleRankLabel}
      {handleRenderImage}
      {handleRenderInfo}
    </StyledContainer>
  )
}
